import { Session } from '@/types/auth';
import { getAccessToken, setAccessToken, getStoredRefreshToken, setStoredRefreshToken } from './token-store';

const API_BASE_URL = typeof window !== 'undefined' ? '/api/mms' : (process.env.NEXT_PUBLIC_API_URL ?? 'http://127.0.0.1:4000');

function buildUrl(path: string): string {
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  if (cleanPath.startsWith('/api/mms/')) return cleanPath;
  return `${API_BASE_URL}${cleanPath}`;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function parseBody(res: Response): Promise<unknown> {
  if (res.status === 204) return undefined;
  const text = await res.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    const trimmed = text.trim();
    if (trimmed.startsWith('<') || trimmed.startsWith('<!DOCTYPE')) {
      throw new ApiError(res.status, `Server returned invalid response (${res.status} ${res.statusText})`);
    }
    return text;
  }
}

function extractMessage(body: unknown, fallback: string): string {
  if (body && typeof body === 'object' && 'message' in body) {
    const message = (body as { message: unknown }).message;
    return Array.isArray(message) ? message.join(', ') : String(message);
  }
  return fallback;
}

/** Used only by login/refresh/logout — never retries on 401 (that would loop). */
export async function authFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(buildUrl(path), {
    ...options,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options.headers },
  });
  const body = await parseBody(res);
  if (!res.ok) {
    throw new ApiError(res.status, extractMessage(body, res.statusText));
  }
  return body as T;
}

export async function refreshSession(): Promise<Session | null> {
  try {
    const storedRefreshToken = getStoredRefreshToken();
    const session = await authFetch<Session>('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refreshToken: storedRefreshToken || undefined }),
    });
    setAccessToken(session.accessToken);
    if (session.refreshToken) {
      setStoredRefreshToken(session.refreshToken);
    }
    return session;
  } catch {
    return null;
  }
}

/**
 * Used for every business endpoint (users/companies/warehouses/...). On a
 * 401 it attempts exactly one silent refresh-and-retry before giving up —
 * covers the case where the access token expired mid-session.
 */
export async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
  allowRetry = true,
): Promise<T> {
  const token = getAccessToken();
  const res = await fetch(buildUrl(path), {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  if (res.status === 401 && allowRetry) {
    const refreshed = await refreshSession();
    if (refreshed) {
      return apiFetch<T>(path, options, false);
    }
  }

  const body = await parseBody(res);
  if (!res.ok) {
    throw new ApiError(res.status, extractMessage(body, res.statusText));
  }
  return body as T;
}

/**
 * multipart/form-data upload (evidence files) — apiFetch always forces
 * `Content-Type: application/json`, which breaks multipart requests (the
 * browser must set the boundary itself), so this is a small parallel
 * helper rather than an apiFetch option. Same auth/refresh-retry behavior.
 */
export async function apiUpload<T>(
  path: string,
  formData: FormData,
  extraHeaders: Record<string, string> = {},
  allowRetry = true,
): Promise<T> {
  const token = getAccessToken();
  const res = await fetch(buildUrl(path), {
    method: 'POST',
    credentials: 'include',
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...extraHeaders },
    body: formData,
  });

  if (res.status === 401 && allowRetry) {
    const refreshed = await refreshSession();
    if (refreshed) {
      return apiUpload<T>(path, formData, extraHeaders, false);
    }
  }

  const body = await parseBody(res);
  if (!res.ok) {
    throw new ApiError(res.status, extractMessage(body, res.statusText));
  }
  return body as T;
}

/** Fetches a binary response (evidence file) with auth, returning a Blob. No JSON parsing/retry-on-401 loop needed for a one-shot view/download. */
export async function apiFetchBlob(path: string): Promise<Blob> {
  const token = getAccessToken();
  const res = await fetch(buildUrl(path), {
    credentials: 'include',
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!res.ok) {
    throw new ApiError(res.status, res.statusText);
  }
  return res.blob();
}
